import React, { useEffect, useState } from 'react';
import { Button, Form, Input, notification, Select, Table, Row, Col } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { postOnBoardingFormThunk, getOnBoardingDataThunk } from '../../../redux/slice/preOnBoarding';
import { FaCheck, FaTimes } from 'react-icons/fa';
import { fetchMasterData } from '../../../redux/slice/masterSlice';
import { useAuth } from "../../../context/AuthContext";
import { HashLoader } from 'react-spinners';
import { trackFeatureAction } from '../../../analytics/googleAnalytics';

const ConsentAsk = () => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const { authState } = useAuth();
  const jwtBearer = authState.token;

  const [loading, setLoading] = useState(false);

  // Use the loading state from the Redux slice
  const isLoadingOnBoardingData = useSelector((state) => state?.preOnBoardingSlice?.isLoading);
  const onboardingData = useSelector((state) => state?.preOnBoardingSlice?.onBoardingFormdata);
  const designations = useSelector((state) => state?.masterSlice?.designations || []);
  const isLoadingDesignations = useSelector((state) => state?.masterSlice?.loading);
  const isErrorDesignations = useSelector((state) => state?.masterSlice?.error);

  // State to hold filters
  const [filters, setFilters] = useState({
    name: '',
    phone: '',
    email: '',
    position: ''
  });

  useEffect(() => {
    trackFeatureAction(
      "PreOnboarding",
      "Employee Consent",
      "open"
    );
  }, []);
  useEffect(() => {
    if (jwtBearer) {
      const fetchData = async () => {
        dispatch(fetchMasterData({ field: 'designation', jwtBearer }));
      };

      fetchData();
    }
  }, [dispatch, jwtBearer]);

  // const handleSubmit = async (values) => {
  //   setLoading(true);
  //   const requestBody = {
  //     name: values.name,
  //     phone: values.phone,
  //     email: values.email,
  //     position: values.position,
  //   };

  //   try {
  //     const response = await dispatch(postOnBoardingFormThunk({ requestBody, jwtBearer })).unwrap();

  //     if (response?.status === 200) {
  //       notification.success({
  //         message: 'Success',
  //         description: `Consent link has been sent successfully.`,
  //         placement: 'topRight',
  //       });
  //       form.resetFields();
  //       dispatch(getOnBoardingDataThunk({ jwtBearer })); // Refresh data after submission
  //     }
  //   } catch (error) {
  //     console.log("in catch", error);
  //     let errorMessage = 'An unexpected error occurred. Please try again later.';

  //     if (error?.code === 400) {
  //       errorMessage = error?.message;
  //     }

  //     notification.error({
  //       message: 'Error',
  //       description: errorMessage,
  //       placement: 'topRight',
  //     });
  //     console.log("errorMessage", errorMessage);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const handleSubmit = async (values) => {
    setLoading(true);
    const requestBody = {
      name: values.name,
      phone: values.phone,
      email: values.email,
      position: values.position,
    };
  
    try {
      const response = await dispatch(postOnBoardingFormThunk({ requestBody, jwtBearer })).unwrap();
  
      if (response?.status === 200) {
        trackFeatureAction(
          "PreOnboarding",
          "Employee Consent",
          "submit"
        );
        notification.success({
          message: 'Success',
          description: `Consent link has been sent successfully.`,
          placement: 'topRight',
        });
        form.resetFields(); // Reset form fields
        dispatch(getOnBoardingDataThunk({ jwtBearer })); // Refresh data after submission
  
        // Reset filters to show all data in the table
        setFilters({
          name: '',
          phone: '',
          email: '',
          position: ''
        });
      }
    } catch (error) {
      console.log("in catch", error);
      let errorMessage = 'An unexpected error occurred. Please try again later.';
  
      if (error?.code === 400) {
        errorMessage = error?.message;
      }
  
      notification.error({
        message: 'Error',
        description: errorMessage,
        placement: 'topRight',
      });
      console.log("errorMessage", errorMessage);
    } finally {
      setLoading(false);
    }
  };
  const handleReset = () => {
    trackFeatureAction(
      "PreOnboarding",
      "Employee Consent",
      "reset"
    );
    form.resetFields();
    setFilters({
      name: '',
      phone: '',
      email: '',
      position: ''
    });
  };

  const filteredData = onboardingData?.filter(item => {
    return (
      (filters.name ? item.name.toLowerCase().includes(filters.name.toLowerCase()) : true) &&
      (filters.phone ? item.phone.includes(filters.phone) : true) &&
      (filters.email ? item.email.toLowerCase().includes(filters.email.toLowerCase()) : true)
    );
  });

  const handleFormChange = (changedValues, allValues) => {
    setFilters(allValues);
  };

  const columns = [
    {
      title: "Phone No.",
      dataIndex: "phone",
      key: "phone",
      align: 'center',
      render: text => <span style={{ fontSize: '16px' }}>{text}</span>,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      align: 'center',
      render: text => <span style={{ fontSize: '16px' }}>{text}</span>,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      align: 'center',
      render: text => <span style={{ fontSize: '16px' }}>{text}</span>,
    },
    {
      title: "Position",
      dataIndex: "position",
      key: "position",
      align: 'center',
      render: text => <span style={{ fontSize: '16px' }}>{text}</span>,
    },
    {
      title: "Link Opened",
      dataIndex: "is_link_opened",
      key: "is_link_opened",
      align: 'center',
      render: (is_link_opened) => (
        is_link_opened ? <FaCheck size={20} color="green" /> : <FaTimes size={20} color="red" />
      ),
    },
    {
      title: "Consent Given",
      dataIndex: "consent_given",
      key: "consent_given",
      align: 'center',
      render: (consent_given) => (
        consent_given ? <FaCheck size={20} color="green" /> : <FaTimes size={20} color="red" />
      ),
    },
  ];

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      paddingTop: '20px',
      paddingRight: '45px',
      paddingLeft: '45px',
      backgroundColor: '#f1f1f1',
      alignItems: 'center'
    }}>
      <Form
        form={form}
        style={{
          width: '100%',
          paddingTop: '30px',
          paddingBottom: '30px',
          paddingLeft: '30px',
          paddingRight: '30px',
          border: '1px solid #ddd',
          borderRadius: '12px',
          backgroundColor: '#ffffff',
          boxShadow: '0 6px 15px rgba(0, 0, 0, 0.1)',
        }}
        layout="vertical"
        onFinish={handleSubmit}
        onValuesChange={handleFormChange}
      >
        <Row gutter={48} className="custom-form">
          <style>
            {`
              .custom-form .ant-input, .custom-form .ant-select-selector {
                height: 40px !important;
                line-height: 40px !important;
              }

              .custom-form .ant-form-item-label > label {
                font-size: 16px !important; 
                font-weight: 600 !important;
              }
            `}
          </style>

          <Col xs={24} sm={12} md={5}>
            <Form.Item
              name="phone"
              label="Phone"
              rules={[
                { required: true, message: 'Please input phone number!' },
                {
                  pattern: /^(?:\+91|91)?[6-9]\d{9}$/,
                  message: 'Enter a valid phone number',
                },
              ]}
            >
              <Input
                placeholder="Enter phone No"
                maxLength={10}
                onInput={(e) => {
                  e.target.value = e.target.value.replace(/\D/g, '');
                }}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={5}>
            <Form.Item
              name="name"
              label="Name"
              rules={[
                { required: true, message: 'Please input name!' },
                { pattern: /^[a-zA-Z.\s]*$/, message: 'Name cannot contain numbers or special characters except dots.' }

              ]}
            >
              <Input placeholder="Enter name" />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={5}>
            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: 'Please input email!' },
                { type: 'email', message: 'Please input a valid email!' }
              ]}
            >
              <Input placeholder="Enter email address" />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={5}>
            <Form.Item
              name="position"
              label="Position"
              rules={[{ required: true, message: 'Please select position!' }]}
            >
              <Select placeholder="Select position">
                {isLoadingDesignations ? (
                  <Select.Option value="loading" disabled>Loading...</Select.Option>
                ) : isErrorDesignations ? (
                  <Select.Option value="error" disabled>Error loading designations</Select.Option>
                ) : (
                  designations?.map((designation, index) => (
                    <Select.Option key={index} value={designation}>
                      {designation}
                    </Select.Option>
                  ))
                )}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} sm={24} md={4} style={{ marginTop: '30px' }}>
            <Form.Item>
              <Button type="primary" htmlType="submit" block loading={loading}>
                Send
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <div style={{ width: '100%', marginTop: '40px' }}>
  <Table
    columns={columns}
    dataSource={filteredData}
    rowKey="email"
    loading={isLoadingOnBoardingData}
    pagination={{ pageSize: 5 }}
    components={{
      header: {
        cell: (props) => {
          return (
            <th {...props} style={{ fontSize: '18px', fontWeight: 'bold', textAlign: 'center' }}>
              {props.children}
            </th>
          );
        }
      }
    }}
  />
</div>

    </div>
  );
};

export default ConsentAsk;